// @flow
import type { Subscription } from 'digicel-kaizen-library';
import { Component } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Separator } from 'trpc-react-native-ui';
import { ConfirmationModal, ValidationButton } from '../../components/common';
import ServiceOptions from '../../components/options/ServiceOptions';
import OrderSummary from '../../components/order/OrderSummary';
import PlanSliders from '../../components/plan/PlanSliders';
import PlanUniqueOffer from '../../components/plan/PlanUniqueOffer';
import StepperPlanSliders from '../../components/plan/StepperPlanSliders';
import config from '../../config';
import I18n from '../../lib/i18n';
import * as Navigation from '../../lib/navigation.js';
import { SelectedVoucherItem } from '../Vouchers/components/SelectedVoucherItem';
import VouchersFrame from '../Vouchers/components/VouchersFrame';

class ChangePlan extends Component<Props, State> {
  state = {
    hasAlreadyResetOfferOnce: false,
    currentStep: 0,
  };

  styles = getStyles();

  componentWillUnmount() {
    if (this.props.setSubscriptionToUpdate) {
      this.props.setSubscriptionToUpdate();
    }
    if (this.props.setCurrentVoucherDetails) {
      this.props.setCurrentVoucherDetails(null);
    }
    // Reset offer options so the next visit re-initializes with correct defaults
    // (ex: DATA at 150Go, others at first option). Uses SET_NEW_OFFER (synchronous
    // reducer) instead of resetOfferState (which triggers sagas with API calls).
    if (config.displayRoamingPlanSliders && this.props.clearOfferState) {
      this.props.clearOfferState();
    }
  }

  componentDidMount() {
    if (this.props.setVoucherDetailsComeFrom) {
      this.props.setVoucherDetailsComeFrom(this.props.orderType ?? '');
    }

    if (
      !this.props.creditableOptions.length &&
      !this.props.serviceOptions.length &&
      this.props.fetchAvailableOptions
    ) {
      this.props.fetchAvailableOptions();
    }

    if (
      this.props.updateSelectedSubscription &&
      this.props.initStateWithOffer
    ) {
      const options = [];      
      this.props.creditableOptions.forEach((creditableOption) => {
        let defaultOption = creditableOption[0];
        if (config.displayRoamingPlanSliders) {
          const type = creditableOption[0]?.type;
          let preferredName = null;
          if (type === 'DATA') {
            preferredName = config.defaultDataOptionName;
          } else if (type === 'VOICE') {
            preferredName = config.defaultVoiceOptionName;
          }
          const preferred = preferredName && creditableOption.find(o => o.name === preferredName);
          if (preferred) defaultOption = preferred;
        }
        options.push(defaultOption);
      });
      // $FlowFixMe
      this.props.initStateWithOffer({ options });
    }
  }

  renderVouchersFrame = () => {
    return this.props.displayVouchersForChangePlan &&
      !this.props.currentVoucherDetails &&
      this.props.filteredAvailableVouchers &&
      this.props.filteredAvailableVouchers.length > 0 ? (
      <VouchersFrame comeFrom="changeplan" />
    ) : null;
  };

  isUniqueOffer = () => {
    return (
      this.props.creditableOptions.length === 3 &&
      this.props.creditableOptions[0].length === 1 &&
      this.props.creditableOptions[1].length === 1 &&
      this.props.creditableOptions[2].length === 1
    );
  };

  displayBody = (isEmpty: boolean) => {
    if (isEmpty) {
      return (
        <View style={this.styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      );
    }
    if (!config.displayRoamingPlanSliders) {
      return (
        <View>
          {this.renderVouchersFrame()}
          {this.isUniqueOffer() ? (
            <PlanUniqueOffer
              withZoneValidity
              creditableOptions={this.props.creditableOptions}
              isChangingPlanDuringTurnBackOn
              orderType={this.props.orderType}
            />
          ) : (
            <PlanSliders
              withZoneValidity
              subscriptionToUpdateOuid={this.props.subscriptionToUpdateOuid}
              isChangingPlanDuringTurnBackOn
              orderType={this.props.orderType}
            />
          )}
          <ServiceOptions />
        </View>
      );
    }

    return (
      <View>
        {this.renderVouchersFrame()}
        {this.isUniqueOffer() ? (
          <>
            <PlanUniqueOffer
              withZoneValidity
              creditableOptions={this.props.creditableOptions}
              isChangingPlanDuringTurnBackOn
              orderType={this.props.orderType}
            />
            <StepperPlanSliders
              withZoneValidity
              subscriptionToUpdateOuid={this.props.subscriptionToUpdateOuid}
              isChangingPlanDuringTurnBackOn
              orderType={this.props.orderType}
              isUniqueOffer={true}
              currentStep={this.state.currentStep}
              onStepChange={(step) => this.setState({ currentStep: step })}
            />
          </>
        ) : (
          <>
            <StepperPlanSliders
              withZoneValidity
              subscriptionToUpdateOuid={this.props.subscriptionToUpdateOuid}
              isChangingPlanDuringTurnBackOn
              orderType={this.props.orderType}
              isUniqueOffer={false}
              currentStep={this.state.currentStep}
              onStepChange={(step) => this.setState({ currentStep: step })}
            />
            {this.state.currentStep === 0 && <ServiceOptions />}
          </>
        )}
      </View>
    );
  };

  handleSubmit = () => {
    if (this.props.updateSelectedSubscription) {
      this.props.updateSelectedSubscription(this.props.newOffer);
    } else if (this.props.changePlan) {
      this.props.changePlan();
    }
  };

  onPressSelectedVoucher = () => {
    Navigation.navigate('voucherDetails', {
      voucherDetails: this.props.currentVoucherDetails,
      disabledButton: true,
    });
  };

  render() {
    const {
      buttonText,
      canChangePlanNow,
      confirmModalText,
      creditableOptions,
      disableNextButton,
      goToConfirmationScene,
      isExemptedFromTax,
      isConfirmationLoading,
      periodicity,
      price = 0,
      serviceOptions,
      subscriptionToUpdateOuid,
      currentVoucherDetails,
      displayVouchersForChangePlan,
    } = this.props;

    const { styles } = this;
    const isEmpty = !creditableOptions.length && !serviceOptions.length;

    const showNextStep =
      config.displayRoamingPlanSliders &&
      this.props.hasRoamingOptions &&
      !this.isUniqueOffer() &&
      this.state.currentStep === 0;

    return (
      <View style={styles.container}>
        <Separator />
        <ScrollView
          contentContainerStyle={[
            styles.scrollViewContent,
            isEmpty && styles.emptyScrollViewContent,
          ]}>
          {this.displayBody(isEmpty)}
        </ScrollView>
        <Separator />
        <OrderSummary
          label={I18n.t('yourNewPlan')}
          totalPrice={price}
          periodicity={periodicity}
          isExemptedFromTax={isExemptedFromTax}
        />
        {displayVouchersForChangePlan && currentVoucherDetails ? (
          <SelectedVoucherItem
            selectedVoucher={currentVoucherDetails}
            onPressSelectedVoucher={this.onPressSelectedVoucher}
          />
        ) : null}
        {showNextStep ? (
          <ValidationButton
            testID="changePlan-next-button"
            onPress={() => this.setState({ currentStep: 1 })}
            text={I18n.t('stepper.next')}
          />
        ) : canChangePlanNow ? (
          <ValidationButton
            testID="changePlan-confirm-button"
            onPress={() => goToConfirmationScene(subscriptionToUpdateOuid)}
            text={buttonText}
            disabled={disableNextButton}
          />
        ) : (
          <ConfirmationModal
            testID="changePlan-open-modal-button"
            onConfirm={this.handleSubmit}
            openModalText={buttonText}
            disabled={disableNextButton}
            confirmContent={confirmModalText}
            isLoading={isConfirmationLoading}
            title={I18n.t('confirmOrder')}
          />
        )}
      </View>
    );
  }
}

const getStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: 'white',
      paddingBottom: Platform.OS === 'ios' ? 40 : 0,
    },
    scrollViewContent: {
      paddingTop: 10,
    },
    emptyScrollViewContent: {
      flex: 1,
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

type State = {
  hasAlreadyResetOfferOnce: boolean,
};

export type FlaggedProps = {
  orderType: String,
};

type Props = {
  buttonText: string,
  canChangePlanNow?: boolean,
  changePlan?: Function,
  confirmModalText: string,
  creditableOptions: Object[][],
  currentSubscription?: Subscription,
  disableNextButton: boolean,
  fetchAvailableOptions: Function,
  goToConfirmationScene: Function,
  initStateWithOffer?: Function,
  isExemptedFromTax: boolean,
  isConfirmationLoading: boolean,
  newOffer?: Object,
  periodicity: Object,
  price: number,
  resetOfferState?: Function,
  serviceOptions: Object[],
  setSubscriptionToUpdate?: Function,
  subscriptionToUpdateOuid: string,
  updateSelectedSubscription?: Function,
  isChangingPlanDuringTurnBackOn?: boolean,
  hasRoamingOptions?: boolean,
  clearOfferState?: Function,
  setCurrentVoucherDetails?: Function,
  currentVoucherDetails?: Object,
  setVoucherDetailsComeFrom?: Function,
  displayVouchersForChangePlan: boolean,
} & FlaggedProps;

export default ChangePlan;
